import type { CSSProperties } from 'react';
import type { CutsceneCamera, CutscenePanel, CutsceneTextBlock, Vector2 } from '@sorrem/shared';

/** The speaker name of the player's own lines; they sit at the bottom center, like thoughts. */
export const PLAYER_SPEAKER = 'You';

/**
 * Where a text block's balloon goes: narration as a caption at the top left,
 * the player's lines and thoughts at the bottom center, titles in the center,
 * and anyone else's lines beside where they stand (`at`), or at the top right
 * when they're off screen.
 */
export type TextPlacement =
    | { area: 'caption' }
    | { area: 'bottom' }
    | { area: 'title' }
    | { area: 'speaker'; style: CSSProperties };

export function resolveTextPlacement(block: CutsceneTextBlock): TextPlacement {
    switch (block.kind) {
        case 'title':
            return { area: 'title' };
        case 'narration':
            return { area: 'caption' };
        case 'thought':
            return { area: 'bottom' };
        case 'dialogue':
            return block.speaker === PLAYER_SPEAKER ? { area: 'bottom' } : resolveSpeakerPlacement(block.at);
        default: {
            const unhandled: never = block.kind;
            return unhandled;
        }
    }
}

/** Another speaker's balloon: beside where they stand, or at the top right when they're off screen. */
function resolveSpeakerPlacement(at: Vector2 | undefined): TextPlacement {
    if (at === undefined) {
        return { area: 'speaker', style: {} };
    }

    const percent = (value: number): number => Number((Math.min(1, Math.max(0, value)) * 100).toFixed(2));
    const { x, y } = at;
    // Beside the speaker on the side with more room, kept inside the frame
    // (--balloon-width is set on the screen); level with their head.
    const keepInside = 'calc(100% - var(--balloon-width) - 16px)';
    const horizontal: CSSProperties = x < 0.5
        ? { left: `clamp(16px, calc(${percent(x)}% + 24px), ${keepInside})` }
        : { right: `clamp(16px, calc(${100 - percent(x)}% + 24px), ${keepInside})` };
    return {
        area: 'speaker',
        style: { ...horizontal, top: `clamp(16px, calc(${percent(y)}% - 40px), calc(100% - 200px))` },
    };
}

/** CSS `object-position` keeping an image's focal point in view; centered by default. */
export function resolveImageObjectPosition(focus: Vector2 | undefined): string {
    const clamp = (value: number): number => Math.min(1, Math.max(0, value));
    const x = focus === undefined ? 0.5 : clamp(focus.x);
    const y = focus === undefined ? 0.5 : clamp(focus.y);
    return `${x * 100}% ${y * 100}%`;
}

/**
 * Custom properties that drive a panel image's camera move (the `camera`
 * animation in CutscenePlayer.module.scss). Zoom is never below 1, so the
 * image always covers the frame.
 */
export function resolveCameraStyle(camera: CutsceneCamera): CSSProperties {
    return {
        '--camera-from-zoom': String(Math.max(1, camera.from.zoom)),
        '--camera-from-origin': resolveImageObjectPosition(camera.from.focus),
        '--camera-to-zoom': String(Math.max(1, camera.to.zoom)),
        '--camera-to-origin': resolveImageObjectPosition(camera.to.focus),
        '--camera-duration': `${Math.max(0, camera.durationMs)}ms`,
    } as CSSProperties;
}

/** A camera's final framing as a still transform, for an image fading out after its move. */
export function resolveCameraEndStyle(camera: CutsceneCamera): CSSProperties {
    return {
        transform: `scale(${Math.max(1, camera.to.zoom)})`,
        transformOrigin: resolveImageObjectPosition(camera.to.focus),
    };
}

/** Milliseconds before a panel advances by itself, or null if it waits for the player. */
export function resolveAutoAdvanceMs(panel: CutscenePanel): number | null {
    const advance = panel.advance ?? 'tap';
    return advance === 'tap' ? null : Math.max(0, advance.autoAfterMs);
}

/** Where the player is in a cutscene: which panel, and which of its lines. */
export interface CutsceneBeat {
    panelIndex: number;
    lineIndex: number;
}

/**
 * The beat before this one, for stepping back through a replay: the previous
 * line, or the last line of the previous panel. Null at the very start.
 */
export function resolvePreviousBeat(panels: readonly CutscenePanel[], beat: CutsceneBeat): CutsceneBeat | null {
    if (beat.lineIndex > 0) {
        return { panelIndex: beat.panelIndex, lineIndex: beat.lineIndex - 1 };
    }
    const previousPanel = panels[beat.panelIndex - 1];
    if (previousPanel === undefined) {
        return null;
    }
    return { panelIndex: beat.panelIndex - 1, lineIndex: Math.max(0, (previousPanel.text?.length ?? 0) - 1) };
}
